import loadScript from 'load-script'

import config from './config'

const GOOGLE_SDK_URL = 'https://apis.google.com/js/api.js'
const MIME = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  gdriveDocument: 'application/vnd.google-apps.document',
}

const onApiLoad = () =>
  new Promise((resolve, reject) => {
    window.gapi.load('auth,picker', {
      callback: resolve,
      onerror: err => reject(new Error('error loading gapi libraries', err)),
    })
  })

const init = () =>
  new Promise((resolve, reject) => {
    loadScript(GOOGLE_SDK_URL, (err, script) => {
      if (err) {
        return reject(new Error('error loading gapi', err))
      }
      return resolve(script)
    })
  })

const authorize = (immediate, scope) =>
  new Promise((resolve, reject) => {
    if (!window.gapi || !window.gapi.auth || !window.google.picker) {
      return reject(new Error('not loaded yet'))
    }

    const token = window.gapi.auth.getToken()
    const oauthToken = token && token.access_token

    if (oauthToken) {
      return resolve(oauthToken)
    }

    return window.gapi.auth.authorize(
      {
        client_id: config.google.oauth.clientId,
        scope,
        immediate,
      },
      result => {
        resolve(result.access_token)
      }
    )
  })

const uploadFile = (blob, metadata, contentType) => {
  const boundary = '-------314159265358979323846'
  const delimiter = `\r\n--${boundary}\r\n`
  const closeDelimiter = `\r\n--${boundary}--`

  const reader = new FileReader()
  reader.readAsBinaryString(blob)
  return new Promise(resolve => {
    reader.onload = () => {
      const base64Data = btoa(reader.result)
      /* eslint-disable prefer-template */
      const multipartRequestBody =
        delimiter +
        'Content-Type: application/json\r\n\r\n' +
        JSON.stringify(metadata) +
        delimiter +
        'Content-Type: ' +
        contentType +
        '\r\n' +
        'Content-Transfer-Encoding: base64\r\n\r\n' +
        base64Data +
        closeDelimiter
      /* eslint-enable prefer-template */

      const request = window.gapi.client.request({
        path: '/upload/drive/v3/files',
        method: 'POST',
        params: { uploadType: 'multipart' },
        headers: {
          'Content-Type': `multipart/related; boundary="${boundary}"`,
        },
        body: multipartRequestBody,
      })

      request.execute(response => resolve(response))
    }
  })
}

const openPicker = (accessToken, title, views, features, mimeTypes) => {
  const builder = new window.google.picker.PickerBuilder()
    .setAppId(config.google.appId)
    .setDeveloperKey(config.google.apiKey)
    .setOAuthToken(accessToken)
    .setTitle(title)
    .setSelectableMimeTypes(mimeTypes.join(','))

  views.forEach(view => builder.addView(view))
  features.forEach(feature => builder.enableFeature(feature))

  return new Promise(resolve => {
    builder.setCallback(data => {
      if (data.action === 'picked') {
        resolve(data)
      }
    })

    const picker = builder.build()
    picker.setVisible(true)
  })
}

const Gapi = {
  init,
  onApiLoad,
  uploadDocx: (name, blob, folderId = false) =>
    authorize(false, config.google.drive.scope).then(() => {
      // upload into a Google Document
      const metadata = { name, mimeType: MIME.gdriveDocument }
      if (folderId) {
        metadata.parents = [folderId]
      }
      // the actual upload must use the correct mime type (docx)
      return uploadFile(blob, metadata, MIME.docx)
    }),
  downloadDocx: id =>
    authorize(false, config.google.drive.scope).then(accessToken => {
      const requestOptions = {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
      }

      // get meta infos about the document at $id
      return fetch(`${config.google.drive.endpoint}${id}`, requestOptions)
        .then(response => response.json())
        .then(payload => {
          let url = payload.downloadUrl
          let filename = payload.title
          if (payload.mimeType === MIME.gdriveDocument) {
            // download Google Docs documents as docx
            url = payload.exportLinks[MIME.docx]
            filename = `${payload.title}.docx`
          }

          // download the actual document
          return fetch(url, requestOptions)
            .then(documentResponse => documentResponse.blob())
            .then(file => ({ file, filename }))
        })
    }),
  openDocumentPicker: () =>
    authorize(false, config.google.drive.scope).then(accessToken => {
      const view = new window.google.picker.View(window.google.picker.ViewId.DOCS)
      view.setMimeTypes(config.upload.mimeTypes.join(','))

      return openPicker(
        accessToken,
        'Choose a document',
        [view],
        [window.google.picker.Feature.NAV_HIDDEN],
        config.upload.mimeTypes
      )
    }),
}

export default Gapi
